/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { LG_SCREEN_MIN, MD_SCREEN_MIN } from '../../../../modules/Styleguide';
import { CDN_PATH } from '../constants';
import {
  PageHeader,
  Section,
  SectionContent,
  SectionHeader,
  ValuePropLabel,
} from '../styled';

export function MonetizationSection() {
  const [quoteRef, inView] = useInView({
    root: null,
    rootMargin: '0px 0px -5% 0px',
    threshold: 0.1,
  });

  return (
    <React.Fragment>
      <StyledSection>
        <SectionContent
          css={css`
            flex-direction: column;
            padding-bottom: 75px;
            @media (min-width: ${LG_SCREEN_MIN}px) {
              padding-bottom: 100px;
            }
          `}
        >
          <ValuePropLabel>Get paid to podcast</ValuePropLabel>
          <PageHeader>
            The first podcast advertising platform for everyone.
          </PageHeader>
        </SectionContent>
        <StyledSectionContent>
          <div
            css={css`
              @media (min-width: ${MD_SCREEN_MIN}px) {
                padding-right: 50px;
              }
            `}
          >
            <SectionHeader>
              Anchor Sponsorships <br />
              <sub>(available in the US)</sub>
            </SectionHeader>
            <p>
              Earn money every time people listen to your episodes using
              Anchor’s easy and optional monetization tools. Record audio ads in
              your own voice and choose where they go in your episodes. There’s
              no minimum audience size required. You decide the brands you want
              to work with and the episodes you want to monetize, meaning you
              have complete control over your content and can seamlessly blend
              promotions into your show.
            </p>
            <p>
              Sponsorships ads are always recorded by you, meaning you have
              complete control over your content and can seamlessly blend
              promotions into your show.
            </p>
            <p>
              <a href="/sponsorships" target="_blank" rel="noopener noreferrer">
                Learn more &rarr;
              </a>
            </p>
          </div>
          <div
            css={css`
              text-align: center;
              @media (max-width: ${MD_SCREEN_MIN}px) {
                order: -1;
                img {
                  max-width: 344px;
                  margin-bottom: 50px;
                }
              }
            `}
          >
            <picture>
              <source
                type="image/webp"
                srcSet={`${CDN_PATH}/sponsorships-screen.webp`}
                media="(min-width: 992px)"
              />
              <source
                srcSet={`${CDN_PATH}/sponsorships-screen.jpg`}
                media="(min-width: 992px)"
              />
              <source
                type="image/webp"
                srcSet={`${CDN_PATH}/sponsorships-screen-mobile.webp`}
              />
              <img
                css={css`
                  flex-direction: column;
                `}
                // @ts-ignore;
                loading="lazy"
                width="100%"
                alt="phone displaying Anchor's Sponsorship tool"
                src={`${CDN_PATH}/sponsorships-screen-mobile.jpg`}
              />
            </picture>
          </div>
        </StyledSectionContent>
      </StyledSection>
      <Section
        css={css`
          background: white;
          overflow: hidden;
        `}
      >
        <div
          css={css`
            margin: 0 auto;
            max-width: 2574px;
            position: relative;
          `}
        >
          <FadeInQuote ref={quoteRef} inView={inView}>
            &#34;We especially love using the Anchor Sponsorships feature.
            It&#39;s so easy to insert ad slots and we love that they&#39;re
            dynamic. It&#39;s such a seamless process and has made monetizing
            the podcast so much easier than we expected.&#34;
            <span>
              - Alex and Christine Shiefer,
              <br />
              hosts of <em>Beach too Sandy, Water Too Wet</em>
            </span>
          </FadeInQuote>
          <picture>
            <source
              type="image/webp"
              srcSet={`${CDN_PATH}/bts-quote-bg.webp`}
              media="(min-width: 992px)"
            />
            <source
              srcSet={`${CDN_PATH}/bts-quote-bg.png`}
              media="(min-width: 992px)"
            />
            <source
              type="image/webp"
              srcSet={`${CDN_PATH}/bts-quote-bg-tablet.webp`}
              media="(min-width: 769px) and (max-width: 991px)"
            />
            <source
              srcSet={`${CDN_PATH}/bts-quote-bg-tablet.png`}
              media="(min-width: 769px) and (max-width: 991px)"
            />
            <source
              type="image/webp"
              srcSet={`${CDN_PATH}/bts-quote-bg-mobile.webp`}
            />
            <img
              // @ts-ignore;
              loading="lazy"
              width="100%"
              alt="hosts of the podcast Beach too Sandy, Water Too Wet"
              src={`${CDN_PATH}/bts-quote-bg-mobile.png`}
            />
          </picture>
        </div>
      </Section>
    </React.Fragment>
  );
}

const StyledSection = styled(Section)`
  background: white;
  h2,
  h4 {
    color: #292f36;
  }
  padding-top: 75px;
  padding-bottom: 75px;
`;

const StyledSectionContent = styled(SectionContent)`
  flex-direction: column;
  padding-right: 70px;
  p {
    margin: 20px 0;
  }
  @media (min-width: ${MD_SCREEN_MIN}px) {
    flex-direction: row;
    p {
      max-width: 300px;
    }
    & > div {
      width: 50%;
    }
  }
`;

type FadeInQuoteProps = {
  inView: boolean;
};

const FadeInQuote = styled.p<FadeInQuoteProps>`
  position: absolute;

  font-weight: bold;
  color: white;
  top: 55%;
  left: 10%;
  max-width: 80vw;
  font-size: 1.4rem;
  line-height: 1.8rem;

  opacity: ${({ inView }: FadeInQuoteProps) => (inView ? '1' : '0')};
  transition: transform ease-in 0.5s, opacity ease-in 0.3s;

  transform: ${({ inView }: FadeInQuoteProps) =>
    inView ? 'translateX(-10px)' : 'translateX(10px)'};

  span {
    display: block;
    padding-top: 25px;
    font-size: 1.2rem;
    line-height: initial;
  }
  @media (min-width: 420px) {
    font-size: 1.8rem;
    line-height: 2.6rem;
    span {
      font-size: 1.6rem;
    }
  }
  @media (min-width: ${MD_SCREEN_MIN}px) {
    top: 69%;
    left: 15%;
    width: 500px;
    font-size: 2rem;
    line-height: 2.8rem;
  }
  @media (min-width: ${LG_SCREEN_MIN}px) {
    top: 25%;
    left: 53%;
    width: 483px;
  }
`;
